import dayjs from 'dayjs';
import type { IntlShape } from 'react-intl';

/**
 * `~1m 09s left` above the 60s boundary (zero-padded seconds), `~50s left` below it
 * (minutes segment dropped) — see the design doc's progress/ETA section. No local
 * countdown: this is called fresh on every poll, never ticked client-side.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const formatEtaSeconds = (
  etaSeconds: number,
  formatMessage: IntlShape['formatMessage']
): string => {
  if (etaSeconds < 60) {
    return formatMessage(
      { id: 'adminProjects.accuracyJobCell.etaSeconds' },
      { seconds: etaSeconds }
    );
  }
  const minutes = Math.floor(etaSeconds / 60);
  const seconds = etaSeconds % 60;
  return formatMessage(
    { id: 'adminProjects.accuracyJobCell.etaMinutesSeconds' },
    { minutes, seconds: String(seconds).padStart(2, '0') }
  );
};

/**
 * Sentence-cases a backend pipeline stage token (`PHENOTYPING`, `EXPORTING`,
 * `SOME_NEW_STAGE`) into a display label (`Phenotyping`, `Exporting`,
 * `Some new stage`). The frontend never enumerates stage names — a rename or a
 * new stage on the backend flows through unchanged. `currentStep` from the
 * progress payload wins when present, otherwise the job `status` is used; the
 * caller-supplied `fallback` is returned when neither carries a token.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const formatAccuracyStageLabel = (
  status: string | null | undefined,
  currentStep: string | null | undefined,
  fallback: string
): string => {
  const token = (currentStep ?? '').trim() || (status ?? '').trim();
  const words = token.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (!words) return fallback;
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Largest applicable unit only — `10d ago`, `3h ago`, `5m ago` — matching the ETA
 * formatter's single-precision style. Falls back to `just now` under a minute.
 */
export const formatTimeAgo = (
  finishedAt: string,
  formatMessage: IntlShape['formatMessage']
): string => {
  const diffSeconds = Math.max(0, dayjs().diff(dayjs(finishedAt), 'second'));

  const days = Math.floor(diffSeconds / 86400);
  if (days >= 1) {
    return formatMessage({ id: 'adminProjects.accuracyJobCell.timeAgoDays' }, { days });
  }

  const hours = Math.floor(diffSeconds / 3600);
  if (hours >= 1) {
    return formatMessage({ id: 'adminProjects.accuracyJobCell.timeAgoHours' }, { hours });
  }

  const minutes = Math.floor(diffSeconds / 60);
  if (minutes >= 1) {
    return formatMessage({ id: 'adminProjects.accuracyJobCell.timeAgoMinutes' }, { minutes });
  }

  return formatMessage({ id: 'adminProjects.accuracyJobCell.timeAgoJustNow' });
};
